import { ColumnSort, flexRender, HeaderGroup, Row, Table } from '@tanstack/react-table';
import { Loader } from 'semantic-ui-react';
import * as React from 'react';
import { DATASET_NAME_TABLE_COLUMN } from '../DatasetFieldsListTable/DatasetFieldsListTableColumns';
import { getFooterGroups, getHeaderGroups } from '../../../shared/table/table-helpers';

const GROUP_SIZE = 3;

interface DataExplorerResultTableParams<DataType> {
  isDataLoaded: boolean;
  isFetching: boolean;
  headerGroups: HeaderGroup<DataType>[];
  sorting: ColumnSort[];
  table: Table<DataType>;
  getTrProps: (state: unknown, rowInfo?: Row<DataType>, column?: undefined) => any;
  Pagination: any;
  noDataText?: React.ReactNode;
}

export function DataExplorerResultTableMarkup<DataType>({
  isDataLoaded,
  isFetching,
  headerGroups,
  sorting,
  table,
  getTrProps,
  Pagination,
  noDataText,
}: DataExplorerResultTableParams<DataType>) {
  const isTableEmpty = table.getRowModel().rows.length === 0 && noDataText;
  return (
    <div className="agreements__content full-height">
      {isDataLoaded && isFetching && (
        <div className="data-table__stale-loader-container">
          <div className="data-table__stale-loader-background"></div>
          <Loader active={true} size="massive" className="data-table__stale-loader-spinner" />
        </div>
      )}
      <div className="block-table-container">
        <div className={`ReactTable intro-step-alphas-3 -highlight ${isTableEmpty ? '-empty' : ''}`}>
          <div className="rt-table" role="grid">
            {getHeaderGroups(headerGroups, sorting)}

            <div
              {...{
                className: 'rt-tbody',
              }}
            >
              {table
                .getRowModel()
                .rows.filter(row => row.depth === 0)
                .map((row, i) => {
                  const groupRowClass = row.getLeafRows().length < GROUP_SIZE ? '-hideAggregated' : '-showAggregated';
                  return (
                    <div className={`rt-tr-group -showRows ${groupRowClass}`} role="rowgroup" key={row.id}>
                      <div
                        {...{
                          key: row.id,
                          className: `rt-tr ${i % 2 === 0 ? '-odd' : '-even'}`,
                          role: 'row',
                          ...getTrProps(undefined, row),
                        }}
                      >
                        {row.getVisibleCells().map(cell => (
                          // eslint-disable-next-line react/jsx-key
                          <div
                            {...{
                              key: cell.id,
                              // @ts-ignore
                              className: `rt-td ${cell.column.columnDef.meta?.className ?? ''} ${
                                cell.column.id === DATASET_NAME_TABLE_COLUMN ? 'rt-expandable rt-pivot ' : ''
                              }`,
                              role: 'gridcell',
                              style: {
                                flex: `${cell.column.getSize()} 0 auto`,
                                width: cell.column.getSize(),
                              },
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        ))}
                      </div>

                      <div className="rt-tr-group -showRows -hideAggregated" role="rowgroup">
                        {row.getLeafRows().map(leafRow => (
                          <div
                            key={leafRow.id}
                            {...{
                              className: `rt-tr ${i % 2 === 0 ? '-odd' : '-even'}`,
                              role: 'row',
                              ...getTrProps(undefined, leafRow),
                            }}
                          >
                            {leafRow.getVisibleCells().map(cell => (
                              // eslint-disable-next-line react/jsx-key
                              <div
                                {...{
                                  key: cell.id,
                                  // @ts-ignore
                                  className: `rt-td`,
                                  role: 'gridcell',
                                  style: {
                                    flex: `${cell.column.getSize()} 0 auto`,
                                    width: cell.column.getSize(),
                                  },
                                }}
                              >
                                {cell.column.columnDef.id !== DATASET_NAME_TABLE_COLUMN &&
                                  flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
            {getFooterGroups(table)}
          </div>
          {isTableEmpty && (
            <div className="rt-noData" role="alert">
              {noDataText}
            </div>
          )}
        </div>
      </div>
      <Pagination />
    </div>
  );
}
